import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Send, Loader2, Lock } from "lucide-react";
import api from "../lib/api";
import { SignalPulse } from "../components/SignalPulse";

export const COUNCIL_PRIVACY_NOTE = "Messages to the Council are private, but not end-to-end encrypted, so any steward can read and reply to them. Use this for disturbances and general questions. For very private matters, please send an Exchange directly to a specific Council member.";

export const CouncilPrivacyNote = () => (
  <p className="font-body text-xs text-zinc-400 leading-relaxed border-l-2 border-[#A855F7]/60 pl-3 flex gap-2" data-testid="council-privacy-note">
    <Lock size={12} className="text-[#A855F7] shrink-0 mt-0.5" /><span>{COUNCIL_PRIVACY_NOTE}</span>
  </p>
);

const Bubble = ({ from, text, at }) => (
  <div className={`max-w-[88%] ${from === "member" ? "ml-auto" : ""}`}>
    <div className={`px-3.5 py-2.5 font-body text-sm whitespace-pre-wrap break-words ${from === "member" ? "bg-[#14A89A]/15 border border-[#14A89A]/30 text-white" : "bg-white/5 border border-white/10 text-zinc-200"}`}>{text}</div>
    <div className={`font-body text-[10px] text-zinc-600 mt-1 ${from === "member" ? "text-right" : ""}`}>{from === "member" ? "You" : "The Council"} · {new Date(at).toLocaleString()}</div>
  </div>
);

const Thread = ({ m, onSent }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { const { data } = await api.post(`/council-messages/${m.id}/reply`, { text: text.trim() }); setText(""); onSent(m.id, data.reply); }
    catch { alert("Could not send. Please try again."); }
    setBusy(false);
  };
  return (
    <div className="border border-white/10 bg-[#121212] p-4 space-y-3" data-testid={`my-council-thread-${m.id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className={`font-body text-[10px] tracking-[0.2em] uppercase ${m.kind === "disturbance" ? "text-[#A855F7]" : "text-[#14A89A]"}`}>{m.kind === "disturbance" ? "⚠ Disturbance" : "✦ Question / Idea / Concern"}</span>
        {m.status === "resolved" && <span className="font-body text-[10px] text-zinc-500 uppercase tracking-wider">Resolved</span>}
      </div>
      <Bubble from="member" text={m.message} at={m.created_at} />
      {m.replies.map((r) => <Bubble key={r.id} from={r.from} text={r.text} at={r.created_at} />)}
      {m.replies.length === 0 && <p className="font-body text-xs text-zinc-600 italic">Waiting for the Council to reply.</p>}
      {m.replies.some((r) => r.from === "council") && (
        <div className="flex gap-2 pt-1">
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Reply to the Council…" data-testid={`my-council-reply-input-${m.id}`}
            className="flex-1 bg-[#0A0A0A] border border-white/15 px-3 py-2 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#14A89A] font-body text-sm resize-none" />
          <button type="button" onClick={send} disabled={busy || !text.trim()} data-testid={`my-council-reply-send-${m.id}`} className="self-end bg-[#14A89A] text-[#0A0A0A] px-3 py-2 disabled:opacity-40" aria-label="Send reply">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          </button>
        </div>
      )}
    </div>
  );
};

export default function CouncilMessages() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/council-messages/mine").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  const onSent = (id, rep) => setItems((xs) => xs.map((m) => (m.id === id ? { ...m, status: "open", replies: [...m.replies, rep] } : m)));
  return (
    <div className="max-w-xl mx-auto px-4 md:px-8 py-8" data-testid="my-council-messages-page">
      <button onClick={() => navigate("/council")} className="flex items-center gap-1 text-zinc-500 hover:text-white text-xs mb-8 font-body"><ArrowLeft size={14} /> Contact the Council</button>
      <h1 className="font-heading text-3xl text-white mb-2 flex items-center gap-2"><ShieldCheck className="text-[#14A89A]" size={26} /> My messages to the Council</h1>
      <div className="mb-6 mt-4"><CouncilPrivacyNote /></div>
      {items === null ? <div className="flex justify-center py-10"><SignalPulse size={38} /></div>
        : items.length === 0 ? <p className="font-body text-sm text-zinc-500" data-testid="my-council-empty">You haven't sent the Council a message yet.</p>
        : <div className="space-y-4">{items.map((m) => <Thread key={m.id} m={m} onSent={onSent} />)}</div>}
    </div>
  );
}
